import * as React from 'react';
import debounce from 'lodash/debounce';
import { DownOutlined, PushpinOutlined } from '@ant-design/icons';
import { Button, Input, Menu, Checkbox, Dropdown, Divider, Space, message } from 'antd';
import { AnchorIcon, ItemContent, MetricSelector, StyledNoData } from './styled';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';

const { Search } = Input;

export interface CustomizeDropdownProps {
  allMetrics: Record<
    string,
    {
      name: string;
      metric: string;
    }[]
  >;
  anchoredMetrics: Record<string, boolean>;
  selectedMetrics: string[];
  mandatoryDimensions?: string[];
  disabled?: boolean;
  maxAnchorCount?: number;
  placeholder?: string;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  onSelectedMetricsChange?: (value: string[]) => void;
  onAnchorMetricsChange?: (value: string) => void;
}

const CustomizeDropdown = (props: CustomizeDropdownProps) => {
  const {
    allMetrics,
    maxAnchorCount,
    selectedMetrics,
    anchoredMetrics,
    disabled,
    mandatoryDimensions = [],
    placeholder = 'Search Dimension / Metrics',
    getPopupContainer,
    onSelectedMetricsChange,
    onAnchorMetricsChange,
  } = props;
  const [innerSelectedMetrics, changeInnerSelectedMetrics] = React.useState(selectedMetrics);
  const [search, setSearch] = React.useState('');
  const [open, setOpenChange] = React.useState(false);

  const metricsMetadata = React.useMemo(() => {
    return search
      ? Object.entries(allMetrics).reduce(
          (acc, [k, v]) => {
            const metrics = v.filter(
              (it) => it.name.toLowerCase().indexOf(search.toLowerCase()) >= 0,
            );

            if (metrics.length) {
              acc[k] = metrics;
            }

            return acc;
          },
          {} as CustomizeDropdownProps['allMetrics'],
        )
      : allMetrics;
  }, [allMetrics, search]);

  const onSearchChange = React.useMemo(
    () =>
      debounce((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
      }, 200),
    [],
  );

  const onChange = (e: CheckboxChangeEvent, metric) => {
    e.stopPropagation();
    let tempMetricCopy: any = innerSelectedMetrics;
    if (e.target.checked) {
      if (tempMetricCopy.indexOf(metric) === -1) {
        tempMetricCopy = [...tempMetricCopy, metric];
      }
      changeInnerSelectedMetrics(tempMetricCopy);
    } else {
      delete anchoredMetrics[metric];
      tempMetricCopy = tempMetricCopy.filter((item) => item !== metric);
      changeInnerSelectedMetrics(tempMetricCopy);
    }
  };

  const resetSearch = () => {
    setSearch('');
  };

  const onApplyMetrics = () => {
    if (innerSelectedMetrics.length < 1) {
      message.warning(`Please select at least one dimension/metric.`);
      return;
    }
    onSelectedMetricsChange?.(innerSelectedMetrics);
    setOpenChange(false);
  };

  const onClearMetrics = () => {
    changeInnerSelectedMetrics(mandatoryDimensions);
  };

  const renderMenus = () => {
    const keys = Object.keys(metricsMetadata);
    const menuGroups = keys.map((key) => {
      if (!metricsMetadata[key] || metricsMetadata[key].length === 0) {
        return null;
      }
      const groupItems = metricsMetadata[key].map((item) => {
        const isSelected = innerSelectedMetrics.some((m) => m === item.metric);
        const isAnchorApplied = innerSelectedMetrics.some((m) => m === item.metric);
        return (
          <div key={item.metric} className="dimension-wrapper">
            <Checkbox
              onChange={(e) => onChange(e, item.metric)}
              checked={isSelected}
              disabled={mandatoryDimensions.includes(item.metric)}
            >
              <ItemContent title={item.name}>{item.name}</ItemContent>
            </Checkbox>
            {isAnchorApplied && (
              <AnchorIcon
                component={PushpinOutlined as React.ForwardRefExoticComponent<any>}
                onClick={(e) => {
                  e.stopPropagation();
                  const anchoredMetricsCount = Object.keys(anchoredMetrics).filter(
                    (metricKey) => anchoredMetrics[metricKey],
                  ).length;
                  if (
                    typeof maxAnchorCount === 'number' &&
                    anchoredMetricsCount === maxAnchorCount &&
                    !anchoredMetrics[item.metric]
                  ) {
                    message.warning(`Cannot pin more than ${maxAnchorCount} columns`);
                    return;
                  }
                  onAnchorMetricsChange?.(item.metric);
                }}
                rotate={anchoredMetrics[item.metric] ? -45 : 0}
              />
            )}
          </div>
        );
      });
      return (
        <Menu.ItemGroup key={key} title={key}>
          {groupItems}
        </Menu.ItemGroup>
      );
    });

    return (
      <MetricSelector>
        <Search
          allowClear
          placeholder={placeholder}
          defaultValue={search}
          onChange={onSearchChange}
        />
        {Object.keys(metricsMetadata).length > 0 ? (
          <Menu className="menu-ul" selectable={false}>
            {menuGroups}
          </Menu>
        ) : (
          <StyledNoData>No dimensions Available.</StyledNoData>
        )}
        <Divider style={{ margin: 0 }} />
        <Space className="bottom-space">
          <Button type="primary" size="small" ghost onClick={onClearMetrics}>
            Clear All
          </Button>
          <Button type="primary" size="small" onClick={onApplyMetrics}>
            Apply
          </Button>
        </Space>
      </MetricSelector>
    );
  };

  React.useEffect(() => {
    changeInnerSelectedMetrics(selectedMetrics);
  }, [selectedMetrics]);

  React.useEffect(() => {
    if (!open) {
      resetSearch();
      Object.keys(anchoredMetrics).map((metricKey) => {
        if (!selectedMetrics.includes(metricKey)) {
          delete anchoredMetrics[metricKey];
        }
      });
      changeInnerSelectedMetrics(selectedMetrics);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dropdown
      open={open}
      disabled={disabled}
      destroyOnHidden
      trigger={['click']}
      popupRender={() => renderMenus()}
      getPopupContainer={getPopupContainer}
      onOpenChange={setOpenChange}
    >
      <Button id="metrics-dropdown-multi-select" disabled={disabled}>
        Customize
        <DownOutlined />
      </Button>
    </Dropdown>
  );
};

export default React.memo(CustomizeDropdown);
